// Customiser lead-capture dialog (2026-07-03). Two modes:
//   kind "question"  - the "stuck helper": shown on exit-intent / dwell so a
//                      customer who's stuck can ask us anything.
//   kind "preorder"  - shown when a customer wanted a sold-out size and is
//                      happy to wait for the restock at 15% off.
//
// Both POST to the worker's /api/ask-question (-> a Zendesk ticket). The
// marketing opt-in is EXPLICIT and, when left ticked, ALSO subscribes the
// email through Shopify's own same-origin newsletter form (/contact,
// form_type=customer) so it flows into Klaviyo via the store's normal sync -
// no server secret, no separate list wiring. The customer can always untick.

import * as React from "react";
import * as ReactDOM from "react-dom";

export type LeadHelperKind = "question" | "preorder";

/** Subscribe an email to the store's marketing via Shopify's native
 *  newsletter form. Same-origin, best-effort, never throws. */
async function subscribeShopifyNewsletter(email: string): Promise<void> {
  try {
    const form = new URLSearchParams();
    form.set("form_type", "customer");
    form.set("utf8", "✓");
    form.set("contact[email]", email);
    form.set("contact[tags]", "newsletter,customiser-lead");
    await fetch("/contact", {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded", accept: "text/html" },
      body: form.toString(),
      credentials: "same-origin",
    });
  } catch {
    /* best-effort */
  }
}

export function LeadHelper({
  kind,
  workerUrl,
  productHandle,
  variantTitle,
  sku,
  onClose,
}: {
  kind: LeadHelperKind;
  workerUrl: string;
  productHandle: string;
  variantTitle?: string;
  sku?: string;
  onClose: () => void;
}) {
  const [email, setEmail] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [optIn, setOptIn] = React.useState(true);
  const [website, setWebsite] = React.useState(""); // honeypot
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const closeRef = React.useRef<HTMLButtonElement | null>(null);

  React.useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  const canSend =
    emailOk && (kind === "preorder" || message.trim().length >= 5) && !busy;

  const submit = async () => {
    if (!canSend) return;
    setBusy(true);
    setError(null);
    try {
      const resp = await fetch(`${workerUrl.replace(/\/$/, "")}/api/ask-question`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          kind,
          email: email.trim(),
          question: message.trim(),
          productHandle,
          variantTitle: variantTitle ?? "",
          sku: sku ?? "",
          pageUrl: typeof location !== "undefined" ? location.href : "",
          optIn,
          website, // honeypot
        }),
      });
      const data = (await resp.json().catch(() => ({}))) as { ok?: boolean };
      if (!data.ok) {
        setError("That didn't go through. Email us at info@rkthreads.com and we'll sort it.");
        setBusy(false);
        return;
      }
      if (optIn) void subscribeShopifyNewsletter(email.trim());
      setDone(true);
    } catch {
      setError("That didn't go through. Email us at info@rkthreads.com and we'll sort it.");
    } finally {
      setBusy(false);
    }
  };

  const heading =
    kind === "preorder"
      ? "Want this size? Pre-order at 15% off"
      : "Stuck on anything?";
  const blurb =
    kind === "preorder"
      ? "We know you wanted this size. It's sold out right now, but if you're happy to wait for the next restock (usually a couple of weeks) we'll take 15% off. Pop your email in and we'll send you a pre-order invoice the moment it's back."
      : "Tell us what's tripping you up and we'll email you back, usually same day. Real people in Melbourne, no bots.";

  // 2026-07-24: portalled to document.body - the customiser root's
  // isolated stacking context (inside the theme section's z-10 wrapper)
  // let the product gallery's z-10 arrow controls and any later section
  // paint OVER this full-screen dialog. Body-level, its own huge z-index
  // finally means what it says.
  return ReactDOM.createPortal(
    <div
      className="rk-pc-lead-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={heading}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="rk-pc-lead">
        <button
          ref={closeRef}
          type="button"
          className="rk-pc-lead__close"
          aria-label="Close"
          onClick={onClose}
        >
          ×
        </button>
        {done ? (
          <div className="rk-pc-lead__done">
            <div className="rk-pc-lead__done-tick" aria-hidden>✓</div>
            <h3 className="rk-pc-lead__title">
              {kind === "preorder" ? "You're on the pre-order list" : "Got it, thanks"}
            </h3>
            <p className="rk-pc-lead__blurb">
              {kind === "preorder"
                ? "We'll email you a 15%-off pre-order invoice as soon as this size is back."
                : "We'll get back to you by email shortly. Keep designing in the meantime."}
            </p>
            <button type="button" className="rk-pc-lead__btn" onClick={onClose}>
              Back to designing
            </button>
          </div>
        ) : (
          <>
            <h3 className="rk-pc-lead__title">{heading}</h3>
            <p className="rk-pc-lead__blurb">{blurb}</p>

            <label className="rk-pc-lead__field">
              <span className="rk-pc-lead__label">Your email</span>
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>

            <label className="rk-pc-lead__field">
              <span className="rk-pc-lead__label">
                {kind === "preorder"
                  ? "Anything we should know? (optional)"
                  : "What's up?"}
              </span>
              <textarea
                rows={kind === "preorder" ? 2 : 3}
                placeholder={
                  kind === "preorder"
                    ? "e.g. need it before a birthday on the 20th"
                    : "e.g. how do I upload two designs?"
                }
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </label>

            {/* honeypot: visually hidden, off the tab order */}
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              style={{ position: "absolute", left: "-9999px", width: 1, height: 1 }}
              aria-hidden
            />

            <label className="rk-pc-lead__optin">
              <input
                type="checkbox"
                checked={optIn}
                onChange={(e) => setOptIn(e.target.checked)}
              />
              <span>
                Email me embroidery tips and the odd offer. You can untick this,
                and unsubscribe any time.
              </span>
            </label>

            {error && <div className="rk-pc-lead__err">{error}</div>}

            <button
              type="button"
              className="rk-pc-lead__btn"
              disabled={!canSend}
              onClick={submit}
            >
              {busy
                ? "Sending…"
                : kind === "preorder"
                  ? "Reserve my pre-order"
                  : "Send it"}
            </button>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
